import type { FieldInputProps, FormikHelpers } from 'formik';
import type { GroupDimensionSwatchType } from '@graphql/generated-base';
import type { CatalogProductItemDetails, CatalogItemTile, MinimalCatalogProductItemDetails } from '../../../../Catalog.types';
import type { UseSharedCatalogMutationReturn } from '../../../../Catalog.controller';
import type { BoxProps, PublicBoxProps } from '@withjoy/joykit';
import type { MinimalProductWithDetails } from '../../../../utils/createTelemetryPayload';
import type { Dispatch, SetStateAction } from 'react';

export interface BuyFormProps
  extends Readonly<
    {
      mode?: string;
      initialActiveSwatchId?: string;
      isAddingItem: boolean;
      isMobile?: boolean;
      showQuantity?: boolean;
      showAllDimensions: boolean;
      relatedItems: Maybe<ProductItem[]>;
      // dimensionFormValues: DimensionFormFields;
      onViewDetailsClick?: React.MouseEventHandler<HTMLButtonElement>;
      addCatalogItem?: UseSharedCatalogMutationReturn['addCatalogItemToRegistry'];
      product: MinimalCatalogProductItemDetails;
      dimensionSpacing: 5 | 6;
      colorSwatchVariant: 'grid' | 'carousel';
      itemPosition?: number;
      selectedImage?: string;
      showSkeleton?: boolean;
      showStickyProductQuickAddBar?: boolean;
      stickyBarIntersectionRef?: React.RefObject<HTMLDivElement>;
      galleryLightboxQuickAddBarRef?: React.RefObject<HTMLDivElement>;
      handleHover?: (id?: string, isHoverExit?: boolean) => void;
      isItemAddedToRegistry: boolean;
      setOpenGiftDialog: Dispatch<SetStateAction<boolean>>;
    } & UseBuyFormArgs &
      PublicBoxProps
  > {}

export interface BuyFormSkeletonProps
  extends Readonly<
    {
      mode: Maybe<'full' | 'compact'>;
      isQuickAddForm?: boolean;
    } & BoxProps
  > {}

export interface DimensionFormFields {
  dimensionType?: string;
}

export interface ProductDetailsFields extends DimensionFormFields {
  qty: number;
}

export type DimensionOption = {
  id: string;
  color: Maybe<string>;
  imageUrl: Maybe<string>;
  value: string;
  label: string;
  notAvailable?: boolean | undefined;
};

export type Dimension = {
  id: string;
  defaultValue: Maybe<DimensionOption>;
  label: string;
  options: ReadonlyArray<DimensionOption>;
  swatchType: GroupDimensionSwatchType;
};

export type onSetDimensionOptionType = (
  variant: MinimalCatalogProductItemDetails,
  dimensionTitle: string | undefined,
  dimensionOption: string | undefined,
  dimensionType: 'dimension' | 'color' | 'image',
  dimensionStringValue: Maybe<string>,
) => void;

export type onSubmitType = (selectedProduct: NonNullable<MinimalProductWithDetails>, formFields: ProductDetailsFields, actions: FormikHelpers<ProductDetailsFields>) => void;

export type UseBuyFormArgs = {
  product: MinimalCatalogProductItemDetails;
  selectedProduct: Maybe<CatalogItemTile>;
  isQuickAddForm?: boolean;
  addCatalogItem?: UseSharedCatalogMutationReturn['addCatalogItemToRegistry'];
  setProductSelected?: (product: MinimalCatalogProductItemDetails) => void;
  itemPosition?: number;
};

export type ProductItem = Maybe<{
  id: string;
  groupDimensionOptions: Array<GroupDimensionOption>;
  storefrontInfo: Maybe<CatalogProductItemDetails['storefrontInfo']>;
  __typename?: 'ProductCatalogItem';
}>;

export type GroupDimensionOption = {
  __typename?: 'ProductCatalogItemGroupDimensionOption';
  id: string;
  displayString: string;
  swatchType: GroupDimensionSwatchType;
  swatchUrl?: Maybe<string>;
  swatchColor?: Maybe<{ __typename?: 'Color'; hex: string; rgba: Array<number>; isLight: boolean }>;
  groupDimension: {
    id: string;
    title: string;
  };
};

export type UseDimensionAvailabilityArgs = {
  relatedItems: Maybe<ProductItem[]>;
  dimensionFormValues: DimensionFormFields;
};

export enum AddCatalogItemPurchaseMethod {
  addToCart = 'addToCart',
  buyNow = 'buyNow',
}

export type AddCatalogItemArgs = {
  // if missing, the item should only be added to the registry
  purchaseMethod?: AddCatalogItemPurchaseMethod | null;
};

type CommonFormCtaProps = {
  quantity: string;
  maxQuantity?: number;
  qtyError?: string | boolean;
  getFieldProps: (nameOrOptions: string) => FieldInputProps<string>;
  onAddCatalogItem: (args?: AddCatalogItemArgs) => void;
  isAddingItem: boolean;
  isFormValid: boolean;
  addToRegistryCopy: Maybe<string>;
  addToCartCopy: Maybe<string>;
  addedToRegistry: Maybe<string>;
  editGift: Maybe<string>;
  buyNowCopy: Maybe<string>;
  storefrontInfo: Maybe<CatalogProductItemDetails['storefrontInfo']>;
  setOpenGiftDialog: Dispatch<SetStateAction<boolean>>;
  buttonText: Maybe<string>;
  isButtonHovered: boolean;
  handleOnMouseHover: () => void;
  handleMouseLeave: () => void;
};

export type QuickAddFormCtaProps = CommonFormCtaProps & {
  showQuantity: boolean;
  isItemAddedToRegistry: boolean;
  // eslint-disable-next-line
  setFieldValue: (field: string, value: any, shouldValidate?: boolean | undefined) => Promise<any> | Promise<void>;
  handleMouseMove: (e: React.MouseEvent<HTMLButtonElement>, buttonRef: React.RefObject<HTMLButtonElement>) => void;
};

export type QuantityStepperProps = {
  // eslint-disable-next-line
  setFieldValue: (field: string, value: any, shouldValidate?: boolean | undefined) => Promise<any> | Promise<void>;
  qty: number;
  getFieldProps: (nameOrOptions: string) => FieldInputProps<string>;
};

export type DefaultBuyFormCtaProps = CommonFormCtaProps & {
  // eslint-disable-next-line
  setFieldValue: (field: string, value: any, shouldValidate?: boolean | undefined) => Promise<any> | Promise<void>;
  storefrontInfo: Maybe<CatalogProductItemDetails['storefrontInfo']>;
  buttonsDividerText: string;
  handleMouseMove: (e: React.MouseEvent<HTMLButtonElement>, buttonRef: React.RefObject<HTMLButtonElement>) => void;
  isItemAddedToRegistry: boolean;
};
